import { eachDayOfInterval, isBefore, isSameDay } from "date-fns";
import { mergeDateRangesDate } from "@ac-expo/utils";
import { useState } from "react";
import { DateRange, DayPickerProps } from "react-day-picker";
import { AcExpoDayPickerPropsBase, CalendarDayPicker } from ".";

export interface PropsMultipleRange extends Omit<DayPickerProps, "mode"> {
  mode: "multiple-range";
  selected?: DateRange[];
  onSelect?: (ranges: DateRange[]) => void;
}

export type MultipleRangeDayPickerProps = AcExpoDayPickerPropsBase &
  PropsMultipleRange;


export const MultipleRangeDayPicker = ({
  modifiers,
  selected: selectedProps = [],
  onSelect,
  ...props
}: Omit<MultipleRangeDayPickerProps, "mode" | "required">) => {
  const [ranges, setRanges] = useState<DateRange[]>(selectedProps);
  const [fromDate, setFromDate] = useState<Date | undefined>();
  const [hoveredRangeIndex, setHoveredRangeIndex] = useState<
    number | undefined
  >();

  const updateRanges = (newRanges: DateRange[]) => {
    const completeRanges = newRanges.filter(
      (range): range is { from: Date; to: Date } => !!range.from && !!range.to
    );
    const mergedRanges = mergeDateRangesDate(completeRanges);
    setRanges(mergedRanges);
    onSelect?.(mergedRanges);
  };

  const handleDateSelect = (date: Date) => {
    if (!fromDate) {
      setFromDate(date);
    } else if (isBefore(date, fromDate)) {
      setFromDate(date);
    } else {
      const newRange: DateRange = {
        from: fromDate,
        to: date,
      };
      updateRanges([...ranges, newRange]);
      setFromDate(undefined);
    }
  };

  const handleRemoveRange = (index: number) => {
    const filtered = ranges.filter((_, i) => i !== index);
    updateRanges(filtered);
  };

  const handleDayClick = (day: Date) => {
    if (hoveredRangeIndex !== undefined && !fromDate) {
      handleRemoveRange(hoveredRangeIndex);
      setHoveredRangeIndex(undefined);
    } else {
      handleDateSelect(day);
    }
  };

  const getRangesDates = () => {
    return ranges.map((range, index) => ({
      index,
      dates: range.from && range.to ? eachDayOfInterval({ start: range.from, end: range.to }) : [],
    }));
  };

  const isDateInHoveredRange = (date: Date) => {
    if (hoveredRangeIndex === undefined || fromDate) return false;
    const rangeData = getRangesDates()[hoveredRangeIndex];
    return rangeData?.dates.some((d) => isSameDay(d, date)) ?? false;
  };

  const allRangesDates = getRangesDates().flatMap((r) => r.dates);

  return (
    <CalendarDayPicker
      modifiers={{
        promotionDates: (date: Date) =>
          allRangesDates.some((d) => isSameDay(d, date)),
        hoveredRangeDate: isDateInHoveredRange,
        selectedStart: (date: Date) => fromDate ? isSameDay(date, fromDate) : false,
        ...modifiers,
      }}
      modifiersClassNames={{
        promotionDates: "bg-blue-100",
        hoveredRangeDate: "bg-red-100",
        selectedStart: "bg-gray-800 text-white",
      }}
      onDayClick={handleDayClick}
      onDayMouseEnter={(day) => {
        const rangeIndex = getRangesDates().findIndex((r) =>
          r.dates.some((d) => isSameDay(d, day))
        );
        if (rangeIndex !== -1) {
          setHoveredRangeIndex(rangeIndex);
        }
      }}
      onDayMouseLeave={() => {
        setHoveredRangeIndex(undefined);
      }}
      customMode="multiple-range"
      {...props}
    />
  );
};

export default MultipleRangeDayPicker;
